import { useEffect, useState } from "react";
import { Row, Col, Button, Card, Form } from "react-bootstrap";
import { EPSelectPicker, EPInput } from "../../../common/controls";
import {
  credentialsForOrganization,
  updateCredentials,
} from "../../../common/utils/api";
import { AxiosResponse } from "axios";
import { axiosGet, axiosPatch } from "../../../common/utils/helper";
import Loader from "../../Loader/loader";
import { toast } from "react-toastify";
import {
  AuthType,
  authTypeArr,
  CommonConstants,
} from "../../../common/utils/CommonConstants";
import CryptoJS from "crypto-js";
import { Whisper, Tooltip } from "rsuite";

interface Credential {
  AuthType: AuthType;
  ClientId: string;
  ClientSecret: string;
  TenantId: string;
}

export default function AddCredentials() {
  const [loading, setLoading] = useState<boolean>(false);
  const [isValidate, setIsValidate] = useState<boolean>(false);
  const [selectedAuthType, setSelectedAuthType] = useState<AuthType>(
    AuthType.DEFAULT
  );
  const [credentialsData, setCredentialsData] = useState<Credential>({
    AuthType: AuthType.DEFAULT,
    ClientId: "",
    ClientSecret: "",
    TenantId: "",
  });
  const AES_SECRET_KEY = process.env.REACT_APP_AES_SECRET as string;

  const encrypt = (data: any) => {
    const ciphertext = CryptoJS.AES.encrypt(
      JSON.stringify(data),
      AES_SECRET_KEY
    ).toString();
    return ciphertext;
  };

  const decrypt = (data: any) => {
    const bytes = CryptoJS.AES.decrypt(data, AES_SECRET_KEY);
    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedData);
  };

  const handleAuthTypeChange = (val: any) => {
    setSelectedAuthType(val);
    setCredentialsData({
      AuthType: val,
      ClientId: "",
      ClientSecret: "",
      TenantId: "",
    });
  };

  const handleOnChange = (value: string, name: keyof Credential) => {
    setCredentialsData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  async function getCredentialsForOrganization(reqUrl: string) {
    const response = (await axiosGet(reqUrl, {})) as AxiosResponse;
    if (response?.status === CommonConstants.API_RESPONSE_SUCCESSFULL) {
      const encryptedCredentialsData = response?.data?.data?.data;

      if (encryptedCredentialsData?.Credentials) {
        const decryptedCredentialsData = decrypt(
          encryptedCredentialsData?.Credentials
        );
        setCredentialsData((data: any) => {
          if (
            encryptedCredentialsData?.CredentialType !== CommonConstants.DEFAULT
          ) {
            return {
              ...data,
              AuthType: encryptedCredentialsData?.AuthType,
              TenantId: decryptedCredentialsData?.TenantId,
              ClientSecret: decryptedCredentialsData?.ClientSecret,
              ClientId: decryptedCredentialsData?.ClientId,
            };
          }
        });
      }
      setSelectedAuthType(encryptedCredentialsData?.AuthType);
    }
  }

  useEffect(() => {
    getCredentialsForOrganization(credentialsForOrganization);
  }, []);

  async function updateCredentialsForOrganization(
    reqUrl: string,
    reqBody: any
  ) {
    setLoading(true);
    let response = (await axiosPatch(reqUrl, { reqBody })) as AxiosResponse;
    if (
      response.status === CommonConstants.API_RESPONSE_SUCCESSFULL &&
      response.data?.status === "success"
    ) {
      setLoading(false);
      toast.success("Auth Configuration verified and updated successfully", {
        position: toast.POSITION.TOP_CENTER,
      });
    } else if (
      response.status == CommonConstants.API_RESPONSE_SUCCESSFULL &&
      response.data?.status === "error"
    ) {
      setLoading(false);
      toast.warning(response?.data?.message, {
        position: toast.POSITION.TOP_CENTER,
      });
    }
  }

  const createEncryptedPayload = () => {
    let payload: any = {};

    payload.AuthType = credentialsData?.AuthType;

    if (credentialsData?.AuthType !== CommonConstants.DEFAULT) {
      payload.Credentials = encrypt({
        TenantId: credentialsData?.TenantId,
        ClientSecret: credentialsData?.ClientSecret,
        ClientId: credentialsData?.ClientId,
      });
    } else {
      payload.Credentials = null;
    }
    return payload;
  };

  const handleSubmit = async () => {
    setIsValidate(true);
    let isEmpty = true;

    if (credentialsData?.AuthType !== CommonConstants.DEFAULT) {
      isEmpty =
        !credentialsData?.ClientId ||
        !credentialsData?.ClientSecret ||
        !credentialsData?.TenantId;
    } else {
      isEmpty = false;
    }

    if (isEmpty) {
      const message = "Field cannot be empty.";
      toast.error(message, {
        position: toast.POSITION.TOP_CENTER,
      });
    } else {
      const payload = createEncryptedPayload();

      await updateCredentialsForOrganization(updateCredentials, payload);
    }
  };

  return loading ? (
    <Loader />
  ) : (
    <Card className="card-managerequest">
      <Card.Body>
        <Form>
          <Row>
            <Form.Group as={Col} lg="12" md="12">
              <div className="form-group">
                <label className="form-label">
                  Select Auth Type <span className="req">* </span>
                </label>
                <Whisper trigger="focus" speaker={<Tooltip>Required</Tooltip>}>
                  <EPSelectPicker
                    isValidate={isValidate}
                    label="AuthType"
                    data={authTypeArr}
                    value={credentialsData?.AuthType}
                    onChange={handleAuthTypeChange}
                  />
                </Whisper>
              </div>
            </Form.Group>

            {selectedAuthType !== CommonConstants?.DEFAULT && (
              <>
                <Form.Group as={Col} lg="12" md="12">
                  <div className="form-group">
                    <label className="form-label">
                      Tenant Id <span className="req">* </span>
                    </label>
                    <Whisper
                      trigger="focus"
                      speaker={<Tooltip>Required</Tooltip>}
                    >
                      <EPInput
                        isValidate={isValidate}
                        minlength={3}
                        maxlength={255}
                        type="password"
                        placeholder="Tenant Id"
                        onChange={(val: string) =>
                          handleOnChange(val, "TenantId")
                        }
                        value={credentialsData?.TenantId}
                      />
                    </Whisper>
                  </div>
                </Form.Group>

                <Form.Group as={Col} lg="12" md="12">
                  <div className="form-group">
                    <label className="form-label">
                      Client Id <span className="req">* </span>
                    </label>
                    <Whisper
                      trigger="focus"
                      speaker={<Tooltip>Required</Tooltip>}
                    >
                      <EPInput
                        isValidate={isValidate}
                        minlength={3}
                        maxlength={255}
                        type="password"
                        placeholder="Client Id"
                        onChange={(val: string) =>
                          handleOnChange(val, "ClientId")
                        }
                        value={credentialsData?.ClientId}
                      />
                    </Whisper>
                  </div>
                </Form.Group>

                <Form.Group as={Col} lg="12" md="12">
                  <div className="form-group">
                    <label className="form-label">
                      Client Secret <span className="req">* </span>
                    </label>
                    <Whisper
                      trigger="focus"
                      speaker={<Tooltip>Required</Tooltip>}
                    >
                      <EPInput
                        isValidate={isValidate}
                        minlength={3}
                        maxlength={255}
                        type="password"
                        placeholder="Client Secret"
                        onChange={(val: string) =>
                          handleOnChange(val, "ClientSecret")
                        }
                        value={credentialsData?.ClientSecret}
                      />
                    </Whisper>
                  </div>
                </Form.Group>
              </>
            )}
          </Row>
          <div className="float-end mt-1">
            <Button
              type="button"
              onClick={() => handleSubmit()}
              className="btn btn-primary"
            >
              Update Auth Type
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}
